/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  type AutocompleteProps,
  Checkbox,
  type FilterOptionsState,
  Skeleton,
  TextField,
  type TextFieldProps,
} from '@mui/material';
import { useMemo } from 'react';

interface Props<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> {
  selected: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['value'];
  fieldLabel: string;
  options: readonly T[];
  handleChange: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['onChange'];
  handleBlur?: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['onBlur'];
  optionLabel?: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['getOptionLabel'];
  isOptionEqualToValue?: AutocompleteProps<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  >['isOptionEqualToValue'];
  onKeyPress?: (key: string) => void;
  onInputChange?: (value: string) => void;
  inputValue?: string;
  disableClearable?: DisableClearable;
  isLoading?: boolean;
  disabled?: boolean;
  error?: boolean;
  freeSolo?: FreeSolo;
  multiple?: Multiple;
  limitTags?: number;
  variant?: TextFieldProps['variant'];
  testId?: string;
  noOptionsText?: string;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
}

/** @deprecated Use tanstack */
function StateAutocomplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  selected,
  fieldLabel,
  options,
  handleChange,
  handleBlur,
  optionLabel,
  isOptionEqualToValue,
  disableClearable,
  onKeyPress,
  onInputChange,
  inputValue,
  multiple,
  limitTags,
  isLoading = false,
  error = false,
  disabled = false,
  freeSolo,
  variant = 'outlined',
  testId,
  noOptionsText,
  filterOptions,
}: Readonly<Props<T, Multiple, DisableClearable, FreeSolo>>) {
  return useMemo(() => {
    if (isLoading) {
      return <Skeleton height={40} />;
    }
    return (
      <Autocomplete
        disableClearable={disableClearable}
        freeSolo={freeSolo}
        size="small"
        data-testid={testId ?? fieldLabel}
        multiple={multiple}
        limitTags={limitTags}
        value={selected}
        onChange={handleChange}
        onKeyUpCapture={(e) => (onKeyPress ? onKeyPress(e.key) : undefined)}
        onBlur={handleBlur}
        options={options}
        getOptionLabel={optionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        disabled={disabled}
        onInputChange={(_, value) => onInputChange?.(value) ?? undefined}
        inputValue={inputValue}
        noOptionsText={noOptionsText}
        filterOptions={filterOptions}
        renderOption={
          multiple
            ? (props, option, { selected: s }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={s}
                  />
                  {optionLabel ? optionLabel(option) : (option as string)}
                </li>
              )
            : undefined
        }
        renderInput={(params) => (
          <TextField {...params} label={fieldLabel} variant={variant} error={error} />
        )}
      />
    );
  }, [
    isLoading,
    disableClearable,
    freeSolo,
    multiple,
    limitTags,
    selected,
    handleChange,
    isOptionEqualToValue,
    handleBlur,
    options,
    optionLabel,
    disabled,
    onKeyPress,
    onInputChange,
    inputValue,
    fieldLabel,
    variant,
    error,
    testId,
    noOptionsText,
    filterOptions,
  ]);
}

export default StateAutocomplete;
